import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { PaymentsQueryKeys } from '~/domains/payments/query';
import { getCryptoDepositAddresses } from '~/services/wallet/wallet.service.api';

type UseCryptoDepositAddressesOptions = {
  enabled?: boolean;
};

export function useCryptoDepositAddresses({
  enabled = true,
}: UseCryptoDepositAddressesOptions = {}) {
  const { user } = useContext(UserContext);

  const query = useQuery({
    queryKey: PaymentsQueryKeys.CRYPTO_DEPOSIT_ADDRESSES(user?.id),
    queryFn: getCryptoDepositAddresses,
    enabled: enabled && !!user?.id,
    // staleTime 0 (NOT Infinity): a connected account's address CAN change server-side — a Whop
    // method-level fanout later corrected to distinct per-family, or a re-provision — so
    // `refetchOnMount` (RQ v4 default true) refetches the current address every time the deposit
    // screen is re-entered. `Infinity` made the data never stale, so re-entry never refetched and a
    // mid-session change was shown stale for the whole session (EDGE-31838). This does NOT re-poll
    // during a single visit — there is no provisioning `refetchInterval` anymore, and
    // `refetchOnWindowFocus` stays false; it only refetches on mount/re-entry. Cheap: for an active
    // account this read serves persisted addresses from the wallet DB, and the GET reuses a VALID
    // GeoComply token (wallet.service.instance.ts) — no re-prompt.
    staleTime: 0,
    // The FRESH GeoComply interceptor already prompts on failure; automatic retries would re-prompt.
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Explicit named return: spreading `...query` would read every observer getter (isFetching /
  // dataUpdatedAt / status flip on every refetch), forcing consumers to re-render each tick under
  // TanStack v4's tracked-render optimization. Reading only the specific stable getters avoids that.
  return {
    data: query.data,
    isLoading: query.isLoading,
    // Prefer stale success over a transient background-refetch error. RQ v4 keeps the prior `data`
    // when a refetch of an already-successful query fails, so with staleTime:0 (which now refetches
    // on every re-entry/reconnect) a network/5xx/geo blip must NOT flip a working address screen to
    // the error branch — report isError only when there is no usable cached data. (EDGE-31838)
    isError: query.isError && query.data === undefined,
    // The plan error itself (not just the isError flag): the container derives per-code copy from it
    // via getApiErrorCode(error), so a KYC/geo/limit fetch failure shows its own message instead of
    // the generic UNKNOWN fallback. Named explicitly (not `...query`) for the same re-render reason
    // as the fields above — `error` only changes when the query's error changes.
    error: query.error,
    refetch: query.refetch,
  };
}
